import { PageHead } from "@generalapplications/html-render/react";
import { EmptyState, Grid, PageArticle } from "@generalapplications/ui";
import { Link } from "react-router-dom";

import { PageHero } from "@/app/common/components/composite/layout/PageHero";
import { StatusBadge } from "@/app/common/components/composite/feedback/StatusBadge";
import { SummaryCard } from "@/app/common/components/composite/list/SummaryCard";
import { Reveal } from "@/app/common/components/composite/motion/Reveal";
import {
  RevealGroup,
  RevealItem,
} from "@/app/common/components/composite/motion/RevealGroup";
import {
  services,
  isListableService,
  isPreviewableService,
  servicesIndexPage,
  sortServicesByStatus,
} from "@/content/services";
import { pageHead } from "@/app/common/functions/seo/pageHead";
import { routes } from "@/app/common/functions/routes/routes";

export function ServicesPage() {
  const head = pageHead({
    path: routes.services.index,
    title: servicesIndexPage.seoTitle,
    description: servicesIndexPage.seoDescription,
  });

  const visibleServices = sortServicesByStatus(
    services.filter(isListableService),
  );

  return (
    <PageArticle rhythm="xl">
      <PageHead head={head} />
      <PageHero
        headline={servicesIndexPage.headline}
        copy={servicesIndexPage.heroCopy}
      />

      {visibleServices.length === 0 ? (
        // Dev-only in practice. While nothing is public, /services is not
        // prerendered, so a production request never reaches this component —
        // the host serves the noindex 404.html catchall
        // (infra/terraform/main.tf) before React runs. This branch exists so
        // the page degrades honestly rather than rendering an empty grid.
        <Reveal>
          <EmptyState
            variant="soft"
            title="These are still being written."
            description={
              <>
                In the meantime, the{" "}
                <Link to={routes.capabilities.index}>capabilities</Link> and{" "}
                <Link to={routes.industries.index}>industries</Link> pages cover
                the same ground from the discipline and environment side.
              </>
            }
          />
        </Reveal>
      ) : (
        <RevealGroup>
          <Grid columns={{ base: 1, md: 2 }} gap="xl">
            {visibleServices.map((service) => (
              <RevealItem key={service.slug}>
                {/* A listed-but-unpublished service is a name, not a
                    destination: the card links only once its page resolves. */}
                <SummaryCard
                  title={service.title}
                  summary={service.summary}
                  href={
                    isPreviewableService(service)
                      ? routes.services.detail(service.slug)
                      : undefined
                  }
                  eyebrow={
                    service.status !== "published" ? (
                      <StatusBadge status={service.status} />
                    ) : undefined
                  }
                />
              </RevealItem>
            ))}
          </Grid>
        </RevealGroup>
      )}
    </PageArticle>
  );
}
